/**
 * Register the wheretopee service worker.
 *
 * Skipped in non-secure contexts (e.g. http://localhost:5173 dev) and when
 * the browser doesn't expose `navigator.serviceWorker`. Calls the supplied
 * `notify` whenever a new SW is `waiting` so the UI can offer a refresh.
 */

export interface RegisterOptions {
  /** notify the user that an update is ready; the click handler is wired here */
  notify: (msg: string, action?: { label: string; onClick: () => void }) => void;
}

export function registerSW(opts: RegisterOptions): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!window.isSecureContext) return;

  const register = async (): Promise<void> => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });

      const notifyUpdate = (worker: ServiceWorker): void => {
        opts.notify("update available - tap to refresh", {
          label: "refresh",
          onClick: () => {
            worker.postMessage("SKIP_WAITING");
          },
        });
      };

      // If a worker is already waiting (e.g. updated in a previous tab), nudge now.
      if (reg.waiting && navigator.serviceWorker.controller) {
        notifyUpdate(reg.waiting);
      }

      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (
            installing.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            notifyUpdate(installing);
          }
        });
      });

      // When the new SW takes control, reload to pick up the fresh shell.
      let reloadOnce = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloadOnce) return;
        reloadOnce = true;
        window.location.reload();
      });
    } catch (err) {
      // Don't break the app if SW registration fails (some browsers, private modes, etc.)
      console.warn("[sw] registration failed", err);
    }
  };

  // Defer registration to after first paint so we don't compete with the map for bandwidth.
  if (document.readyState === "complete") {
    setTimeout(register, 0);
  } else {
    window.addEventListener("load", () => setTimeout(register, 0));
  }
}
